<template>
  <div class="grow bg-[#F8FAFC]">

    <!-- Navigation -->
    <header class="bg-[#F8FAFC]">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <span class="text-xl font-extrabold text-[#0F172A] tracking-tight">
            Giraaf<span :style="gradientText">Desk</span>
          </span>
          <span
            class="hidden sm:inline-block text-[11px] bg-[#E0F2FE] text-[#075985] font-bold px-2.5 py-0.5 rounded-full border border-[#BAE6FD]">
            Helpdesk & Support
          </span>
        </div>

        <nav aria-label="Navigation principale" class="flex items-center gap-1 sm:gap-2">
          <button type="button" @click="scrollTo('fonctionnalites')"
            class="hidden md:inline-block px-3 py-2 rounded-xl text-xs font-semibold text-[#475569] hover:text-[#0F172A] hover:bg-[#E2E8F0]/60 transition cursor-pointer">
            Fonctionnalités
          </button>
          <button type="button" @click="scrollTo('fonctionnement')"
            class="hidden md:inline-block px-3 py-2 rounded-xl text-xs font-semibold text-[#475569] hover:text-[#0F172A] hover:bg-[#E2E8F0]/60 transition cursor-pointer">
            Comment ça marche
          </button>
          <button type="button" @click="$emit('open-app')" :style="gradientBg"
            class="text-white font-bold text-xs sm:text-sm px-4 py-2 rounded-xl shadow-xs hover:opacity-95 transition cursor-pointer">
            Ouvrir le helpdesk
          </button>
        </nav>
      </div>
    </header>

    <main>
      <!-- Hero : titre + démo interactive -->
      <section
        class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20 lg:pt-16 lg:pb-28 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div>
          <h1 class="text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold tracking-tight text-[#0F172A] leading-[1.06]">
            Suivez chaque demande jusqu'à sa résolution.
          </h1>
          <p class="mt-5 text-base sm:text-lg text-[#475569] leading-relaxed max-w-[34rem]">
            GiraafDesk rassemble vos tickets, leurs priorités et les réponses apportées au même endroit. Vous voyez
            d'un coup d'œil ce qui est ouvert, ce qui attend et ce qui est réglé.
          </p>

          <div class="mt-8 flex flex-wrap items-center gap-3">
            <button type="button" @click="$emit('open-app')" :style="gradientBg"
              class="text-white font-bold text-sm px-6 py-3 rounded-xl shadow-xs hover:opacity-95 transition cursor-pointer">
              Ouvrir le helpdesk
            </button>
            <button type="button" @click="scrollTo('fonctionnalites')"
              class="bg-white text-[#0F172A] font-bold text-sm px-6 py-3 rounded-xl border border-[#CBD5E1] hover:border-[#94A3B8] transition cursor-pointer">
              Voir les fonctionnalités
            </button>
          </div>

          <p class="mt-5 text-xs text-[#64748B] max-w-md leading-relaxed">
            Interface en français, sans configuration : ouvrez le helpdesk et créez votre premier ticket.
          </p>
        </div>

        <!-- Démo : l'utilisateur fait avancer les statuts -->
        <div>
          <div
            class="rounded-2xl bg-white border border-[#E2E8F0] overflow-hidden shadow-[0_24px_60px_-24px_rgba(15,23,42,0.28)]">
            <div class="flex items-center gap-2 px-4 h-10 border-b border-[#E2E8F0] bg-[#F8FAFC]">
              <span class="w-2.5 h-2.5 rounded-full bg-[#FCA5A5]" aria-hidden="true"></span>
              <span class="w-2.5 h-2.5 rounded-full bg-[#FCD34D]" aria-hidden="true"></span>
              <span class="w-2.5 h-2.5 rounded-full bg-[#86EFAC]" aria-hidden="true"></span>
              <span class="ml-2 text-[11px] font-semibold text-[#64748B]">Aperçu des tickets</span>
            </div>

            <div class="grid grid-cols-4 border-b border-[#E2E8F0]" aria-live="polite">
              <div v-for="(c, i) in demoCounts" :key="c.label" class="px-4 py-3"
                :class="i > 0 ? 'border-l border-[#E2E8F0]' : ''">
                <span class="block text-[10px] font-semibold text-[#94A3B8] leading-tight">{{ c.label }}</span>
                <span class="block text-xl font-extrabold mt-0.5" :style="{ color: c.color }">{{ c.value }}</span>
              </div>
            </div>

            <ul>
              <li v-for="(t, i) in demoTickets" :key="t.id" class="flex items-center gap-3 px-4 py-3"
                :class="i > 0 ? 'border-t border-[#E2E8F0]' : ''">
                <div class="min-w-0 flex-1">
                  <p class="font-mono text-[10px] font-bold text-[#64748B]">{{ t.id }}</p>
                  <p class="text-xs font-bold text-[#0F172A] truncate">{{ t.subject }}</p>
                  <p class="text-[11px] text-[#64748B] truncate">{{ t.client }}</p>
                </div>
                <span class="hidden sm:inline-block px-2.5 py-0.5 rounded-full text-[10px] font-semibold"
                  :class="priorityClass(t.priority)">{{ t.priority }}</span>
                <button type="button" @click="nextStatus(t)"
                  :aria-label="`Statut du ticket ${t.id} : ${t.status}. Cliquer pour le faire avancer.`"
                  class="min-w-[6.5rem] text-center px-3 py-1.5 rounded-full border text-[11px] font-bold cursor-pointer transition active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0284C7]/40"
                  :class="statusClass(t.status)">
                  {{ t.status }}
                </button>
              </li>
            </ul>
          </div>
          <p class="mt-3 text-xs text-[#64748B] text-center">
            Essayez : cliquez sur un statut pour faire avancer le ticket.
          </p>
        </div>
      </section>

      <!-- Fonctionnalités -->
      <section id="fonctionnalites" class="bg-white border-t border-b border-[#E2E8F0]">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-12 gap-12">
          <div class="lg:col-span-5 lg:sticky lg:top-8 self-start">
            <h2 class="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] leading-tight">
              Tout pour traiter un ticket, rien de superflu.
            </h2>
            <p class="mt-4 text-[#475569] leading-relaxed max-w-md">
              GiraafDesk reste volontairement simple : une liste, des filtres et une fiche de résolution. Votre équipe
              le prend en main sans formation.
            </p>
          </div>

          <ul class="lg:col-span-7">
            <li v-for="(f, i) in features" :key="f.title" class="flex gap-4 py-6"
              :class="i > 0 ? 'border-t border-[#E2E8F0]' : ''">
              <span class="w-10 h-10 shrink-0 rounded-xl bg-[#E0F2FE] text-[#0284C7] flex items-center justify-center"
                aria-hidden="true">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="f.icon" />
                </svg>
              </span>
              <div>
                <h3 class="text-base font-bold text-[#0F172A]">{{ f.title }}</h3>
                <p class="mt-1 text-sm text-[#475569] leading-relaxed max-w-lg">{{ f.text }}</p>
              </div>
            </li>
          </ul>
        </div>
      </section>

      <!-- Comment ça marche -->
      <section id="fonctionnement" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 class="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] leading-tight max-w-xl">
          De la demande à la solution, en trois étapes.
        </h2>

        <ol class="mt-12 grid md:grid-cols-3 gap-10 md:gap-8">
          <li v-for="(s, i) in steps" :key="s.title">
            <div class="flex items-center gap-3">
              <span :style="gradientBg"
                class="w-9 h-9 shrink-0 rounded-full text-white text-sm font-bold flex items-center justify-center">
                {{ i + 1 }}
              </span>
              <span v-if="i < steps.length - 1" class="hidden md:block h-px flex-1 bg-[#CBD5E1]"
                aria-hidden="true"></span>
            </div>
            <h3 class="mt-4 text-base font-bold text-[#0F172A]">{{ s.title }}</h3>
            <p class="mt-1.5 text-sm text-[#475569] leading-relaxed max-w-xs">{{ s.text }}</p>
          </li>
        </ol>
      </section>

      <!-- Appel à l'action -->
      <section class="px-4 sm:px-6 lg:px-8 pb-20">
        <div :style="gradientBg"
          class="max-w-7xl mx-auto rounded-3xl px-8 py-12 sm:px-14 sm:py-14 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <h2 class="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
              Votre premier ticket vous attend.
            </h2>
            <p class="mt-2 text-[#E0E7FF] max-w-lg leading-relaxed">
              Ouvrez le helpdesk, créez un ticket et suivez-le jusqu'à sa résolution.
            </p>
          </div>
          <button type="button" @click="$emit('open-app')"
            class="shrink-0 bg-white text-[#0F172A] font-bold text-sm px-6 py-3 rounded-xl hover:bg-[#F1F5F9] transition cursor-pointer">
            Ouvrir le helpdesk
          </button>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
export default {
  name: 'HomePage',
  emits: ['open-app'],
  data() {
    return {
      // Même dégradé que le bouton « Nouveau ticket » du helpdesk.
      // L'ordre compte : "background" avant "background-clip".
      gradientBg: {
        background: 'linear-gradient(135deg, #0284C7 0%, #2563EB 50%, #4F46E5 100%)'
      },
      gradientText: {
        background: 'linear-gradient(135deg, #0284C7 0%, #4F46E5 100%)',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        color: 'transparent'
      },
      statusOrder: ['Ouvert', 'En attente', 'Résolu', 'Fermé'],
      // Données d'illustration : elles n'existent que sur cette page.
      demoTickets: [
        { id: 'DESK-2026-0004', subject: 'Impossible de télécharger un fichier', client: 'client1@example.com', priority: 'Basse', status: 'Ouvert' },
        { id: 'DESK-2026-0003', subject: 'Demande de nouvelle fonctionnalité', client: 'client3@example.com', priority: 'Normale', status: 'En attente' },
        { id: 'DESK-2026-0002', subject: 'Erreur sur la page de paiement', client: 'client2@example.com', priority: 'Urgente', status: 'Fermé' },
        { id: 'DESK-2026-0001', subject: 'Problème avec la connexion', client: 'client1@example.com', priority: 'Haute', status: 'Ouvert' }
      ],
      features: [
        {
          title: 'Des tickets numérotés',
          text: "Chaque demande reçoit un numéro unique, du type DESK-2026-0001, que vous et votre client pouvez citer sans ambiguïté.",
          icon: 'M7 20l4-16m2 16l4-16M6 9h14M4 15h14'
        },
        {
          title: 'Priorité et statut lisibles',
          text: "De basse à urgente, d'ouvert à fermé : deux réglages simples, visibles d'un coup d'œil dans la liste.",
          icon: 'M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9'
        },
        {
          title: 'Recherche et filtres instantanés',
          text: 'Retrouvez un ticket par son numéro, son sujet ou l\'e-mail du client, ou filtrez la liste par statut.',
          icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
        },
        {
          title: 'Une réponse de résolution',
          text: "Consignez la solution apportée dans le ticket avant de le passer en résolu : elle reste attachée à la demande.",
          icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
        },
        {
          title: "Une vue d'ensemble immédiate",
          text: "Total, ouverts, en attente, résolus : les compteurs se mettent à jour à chaque changement de statut.",
          icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
        }
      ],
      steps: [
        { title: 'Ouvrez un ticket', text: "Indiquez l'e-mail du client, le sujet, l'urgence et la description du problème." },
        { title: 'Suivez et priorisez', text: "Faites évoluer le statut depuis la liste au fil de l'avancement du traitement." },
        { title: 'Résolvez et documentez', text: 'Notez la solution apportée, puis passez le ticket en résolu.' }
      ]
    }
  },
  computed: {
    demoCounts() {
      const count = (...names) => this.demoTickets.filter(t => names.includes(t.status)).length
      return [
        { label: 'Total', value: this.demoTickets.length, color: '#0F172A' },
        { label: 'Ouverts', value: count('Ouvert'), color: '#D97706' },
        { label: 'En attente', value: count('En attente'), color: '#0284C7' },
        { label: 'Résolus', value: count('Résolu', 'Fermé'), color: '#059669' }
      ]
    }
  },
  methods: {
    scrollTo(id) {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    },
    nextStatus(ticket) {
      const next = (this.statusOrder.indexOf(ticket.status) + 1) % this.statusOrder.length
      ticket.status = this.statusOrder[next]
    },
    priorityClass(priority) {
      const map = {
        Urgente: 'bg-[#FFE4E6] text-[#BE123C]',
        Haute: 'bg-[#FEF3C7] text-[#92400E]',
        Normale: 'bg-[#E0F2FE] text-[#0369A1]',
        Basse: 'bg-[#F1F5F9] text-[#475569]'
      }
      return map[priority] || 'bg-[#F1F5F9] text-[#475569]'
    },
    statusClass(status) {
      const map = {
        Ouvert: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]',
        'En attente': 'bg-[#E0F2FE] text-[#075985] border-[#BAE6FD]',
        Résolu: 'bg-[#D1FAE5] text-[#047857] border-[#A7F3D0]',
        Fermé: 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]'
      }
      return map[status] || 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]'
    }
  }
}
</script>
